import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { buildOrderInfoPayload, mapOrderInfo, sumOrderInfos } from "./orderMappers";
import { quoteOrder } from "./orderService";

const quoteOneCart = async ({ cartId, deliveryTypeId, deliveryOn, deliveryAddress, couponCode }) => {
  const payload = buildOrderInfoPayload({
    cartId,
    deliveryTypeId,
    deliveryOn,
    deliveryAddress,
    couponCode,
  });

  try {
    return { cartId, info: mapOrderInfo(await quoteOrder(payload)), couponRejected: false };
  } catch (error) {
    if (!couponCode) throw error;

    const fallback = mapOrderInfo(
      await quoteOrder(
        buildOrderInfoPayload({ cartId, deliveryTypeId, deliveryOn, deliveryAddress })
      )
    );

    return { cartId, info: fallback, couponRejected: true };
  }
};

const useCheckoutQuote = ({ carts, deliveryTypeIdForCart, address, deliveryOn } = {}) => {
  const [perSupplierQuotes, setPerSupplierQuotes] = useState([]);
  const [isQuoting, setIsQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [couponCode, setCouponCode] = useState(null);
  const [couponMessage, setCouponMessage] = useState(null);
  const requestRef = useRef(0);

  const cartIds = (carts || []).map((cart) => cart.cartId);
  const cartIdsKey = cartIds.join(",");

  const canQuote =
    Boolean(address) &&
    cartIds.length > 0 &&
    cartIds.every((cartId) => Number.isFinite(deliveryTypeIdForCart(cartId)));

  useEffect(() => {
    let cancelled = false;

    if (!canQuote) {
      setPerSupplierQuotes([]);
      setIsQuoting(false);

      return undefined;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setIsQuoting(true);
    setQuoteError(null);

    Promise.all(
      carts.map((cart) =>
        quoteOneCart({
          cartId: cart.cartId,
          deliveryTypeId: deliveryTypeIdForCart(cart.cartId),
          deliveryOn,
          deliveryAddress: address,
          couponCode,
        })
      )
    )
      .then((results) => {
        if (cancelled || requestId !== requestRef.current) return;

        setPerSupplierQuotes(results);

        if (results.some((row) => row.couponRejected)) {
          setCouponCode(null);
          setCouponMessage(
            "This voucher does not apply to every supplier in your cart and was removed."
          );
        }
      })
      .catch((error) => {
        if (cancelled || requestId !== requestRef.current) return;

        setQuoteError(error);
        setPerSupplierQuotes([]);
      })
      .finally(() => {
        if (!cancelled && requestId === requestRef.current) setIsQuoting(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canQuote, cartIdsKey, address, deliveryOn, couponCode]);

  const applyCoupon = useCallback((code) => {
    setCouponMessage(null);
    setCouponCode(code);
  }, []);

  const removeCoupon = useCallback(() => {
    setCouponCode(null);
    setCouponMessage(null);
  }, []);

  const quote = useMemo(
    () => sumOrderInfos(perSupplierQuotes.map((row) => row.info)),
    [perSupplierQuotes]
  );

  return {
    quote,
    perSupplierQuotes,
    isQuoting,
    quoteError,
    couponCode,
    couponMessage,
    applyCoupon,
    removeCoupon,
  };
};

export default useCheckoutQuote;
